// Page-level answer synchronisation for the versioned save contract.
//
// `local` is what the student has typed or selected (kept immediately in page
// state, whichever question is on screen). `server` is what the backend has
// acknowledged: { questionId: { text, revision } }. A question only counts as
// saved when its local value equals the acknowledged text, so an earlier
// request finishing can never mark newer edits as saved.

export const answerText = (value) => (typeof value === "string" ? value : "");

export const serverEntry = (server, questionId) =>
  server?.[questionId] || { text: "", revision: 0 };

export const createServerAnswers = (answers = {}, revisions = {}) => {
  const questionIds = new Set([...Object.keys(answers || {}), ...Object.keys(revisions || {})]);
  return Object.fromEntries([...questionIds].map((questionId) => [questionId, {
    text: answerText(answers?.[questionId]),
    revision: Number.isSafeInteger(revisions?.[questionId]) ? revisions[questionId] : 0,
  }]));
};

// Changes for every question whose local value differs from the acknowledged
// one. Questions with an unresolved conflict are held back until the student
// chooses which version to keep.
export const pendingChanges = (local, server, questionIds, conflicts = {}) => {
  const changes = {};
  questionIds.forEach((questionId) => {
    if (Object.prototype.hasOwnProperty.call(conflicts, questionId)) return;
    const text = answerText(local?.[questionId]);
    const entry = serverEntry(server, questionId);
    if (text !== entry.text) changes[questionId] = { text, baseRevision: entry.revision };
  });
  return changes;
};

export const applySaveResults = (server, changes, results) => {
  const next = { ...server };
  const conflicts = {};
  Object.entries(changes).forEach(([questionId, change]) => {
    const result = results[questionId];
    if (result.status === "saved") {
      next[questionId] = { text: change.text, revision: result.revision };
    } else {
      next[questionId] = { text: result.text, revision: result.revision };
      conflicts[questionId] = result.text;
    }
  });
  return { server: next, conflicts };
};

// One of: "saved", "pending" (changed, not sent yet), "saving", "failed",
// "conflict" (changed in another window; the student must choose).
export const questionSaveState = (questionId, { local, server, inFlight = {}, failed = {}, conflicts = {} }) => {
  if (Object.prototype.hasOwnProperty.call(conflicts, questionId)) return "conflict";
  if (Object.prototype.hasOwnProperty.call(inFlight, questionId)) return "saving";
  if (answerText(local?.[questionId]) === serverEntry(server, questionId).text) return "saved";
  return failed[questionId] ? "failed" : "pending";
};

const OVERALL_PRIORITY = ["conflict", "failed", "saving", "pending"];

export const overallSaveState = (states) =>
  OVERALL_PRIORITY.find((state) => states.includes(state)) || "saved";

// Answered is about content, not saving: a non-blank written answer, or an MCQ
// selection that matches a visible option.
export const isAnswered = (question, value) => {
  const text = answerText(value);
  if (question?.questionType === "multipleChoice") {
    return (question.options || []).some((option) => option.id === text);
  }
  return text.trim().length > 0;
};

export const answerRevisionMap = (server, questionIds) =>
  Object.fromEntries(questionIds.map((questionId) => [questionId, serverEntry(server, questionId).revision]));
